"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trackInteraction } from "@/utils/trackInteraction";
import { getCookieValue } from "@/utils/getCookieValue";
import { getMetaData } from "@/utils/getMetaData";

function getScrollPercent(): number {
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  const docHeight = Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight,
  );
  const percent = (scrollTop / (docHeight - window.innerHeight)) * 100;
  return Math.min(100, Math.round(percent));
}

export default function useScrollDepthTracker() {
  const pathname = usePathname();
  const lastTrackedDepth = useRef<number>(0);
  const rafPending = useRef<boolean>(false);

  useEffect(() => {
    lastTrackedDepth.current = 0;
    rafPending.current = false;

    const handleScroll = () => {
      if (rafPending.current) return;
      rafPending.current = true;

      requestAnimationFrame(() => {
        rafPending.current = false;
        const scrollPercent = getScrollPercent();
        const roundedTo10 = Math.floor(scrollPercent / 10) * 10;

        if (roundedTo10 > lastTrackedDepth.current) {
          lastTrackedDepth.current = roundedTo10;

          trackInteraction(
            {
              event_label: "user_scroll",
              scroll_percent: roundedTo10,
              page_path: window.location.pathname,
              page_url: window.location.href,
              referrer: sessionStorage.getItem("isov_referrer") ?? "direct",
              timestamp: new Date().toISOString(),
              identity: getCookieValue("hubspotutk"),
              content_tags: getMetaData("content-tag"),
            },
            "user_scroll",
          );
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      rafPending.current = false;
    };
  }, [pathname]);
}
